import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TeamSettingsModal = ({ onClose }) => {
    const { selectedTeam, fetchTeams, setSelectedTeam, user } = useAuth();

    const [activeTab, setActiveTab] = useState('members'); // 'members' or 'create'

    // Create Team state
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamSlug, setNewTeamSlug] = useState('');
    const [creating, setCreating] = useState(false);

    // Invite Member state
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviting, setInviting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState('');

    // Members list
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [busyMemberId, setBusyMemberId] = useState(null);

    const canManageMembers = ['owner', 'admin'].includes(selectedTeam?.role) || user?.platform_role?.slug === 'super_admin';

    useEffect(() => {
        if (selectedTeam && activeTab === 'members') {
            loadMembers(selectedTeam.id);
        }
    }, [selectedTeam, activeTab]);

    const loadMembers = async (teamId) => {
        try {
            setLoadingMembers(true);
            const data = await api.getTeamMembers(teamId);
            setMembers(data.members || []);
        } catch (err) {
            setErrorMessage(err.message || 'Failed to load team members');
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleCreateTeam = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const data = await api.createTeam(newTeamName, newTeamSlug);
            await fetchTeams(); // Refresh global teams list
            setSelectedTeam({ ...data.team, role: 'owner' }); // Auto switch to new team
            setActiveTab('members');
            setNewTeamName('');
            setNewTeamSlug('');
        } catch (err) {
            setErrorMessage(err.message || 'Failed to create team');
        } finally {
            setCreating(false);
        }
    };

    const handleInviteUser = async (e) => {
        e.preventDefault();
        if (!selectedTeam) return;
        setInviting(true);
        setInviteSuccess('');
        setErrorMessage('');
        try {
            const { token } = await api.inviteUser(selectedTeam.id, inviteEmail, inviteRole);
            setInviteSuccess(`Invite sent! Token: ${token}`);
            setInviteEmail('');
        } catch (err) {
            setErrorMessage(err.message || 'Failed to invite user');
        } finally {
            setInviting(false);
        }
    };

    const handleRoleChange = async (member, role) => {
        if (!selectedTeam || member.role === role) return;
        const action = role === 'owner'
            ? `Transfer ownership to ${member.email}? You will become an admin.`
            : `Change ${member.email} from ${member.role} to ${role}?`;
        if (!window.confirm(action)) return;

        setBusyMemberId(member.id);
        setErrorMessage('');
        setStatusMessage('');
        try {
            if (role === 'owner') {
                await api.transferTeamOwnership(selectedTeam.id, member.id);
            } else {
                await api.updateTeamMemberRole(selectedTeam.id, member.id, role);
            }
            setStatusMessage(role === 'owner' ? 'Team ownership transferred.' : 'Member role updated.');
            await loadMembers(selectedTeam.id);
            await fetchTeams();
        } catch (err) {
            setErrorMessage(err.message || 'Failed to update member role');
        } finally {
            setBusyMemberId(null);
        }
    };

    const handleRemoveMember = async (member) => {
        if (!selectedTeam || !window.confirm(`Remove ${member.email} from this team?`)) return;
        setBusyMemberId(member.id);
        setErrorMessage('');
        setStatusMessage('');
        try {
            await api.removeTeamMember(selectedTeam.id, member.id);
            setStatusMessage('Member removed.');
            await loadMembers(selectedTeam.id);
        } catch (err) {
            setErrorMessage(err.message || 'Failed to remove member');
        } finally {
            setBusyMemberId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 border-l-4 border-blue-600 pl-3">
                        Team Settings
                    </h2>
                    <button onClick={onClose} aria-label="Close team settings" className="text-gray-400 hover:text-gray-600 focus:outline-none">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('members')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'members' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        Manage Members
                    </button>
                    <button
                        onClick={() => setActiveTab('create')}
                        className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'create' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        Create New Team
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {errorMessage && <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{errorMessage}</div>}
                    {statusMessage && <div role="status" aria-live="polite" className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">{statusMessage}</div>}
                    {activeTab === 'create' && (
                        <div className="max-w-md mx-auto">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Create a new workspace</h3>
                            <p className="text-sm text-gray-500 mb-6">Teams allow you to collaborate on projects, share environment variables, and manage billing together.</p>

                            <form onSubmit={handleCreateTeam} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={newTeamName}
                                        onChange={(e) => {
                                            setNewTeamName(e.target.value);
                                            if (!newTeamSlug) setNewTeamSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-'));
                                        }}
                                        placeholder="e.g. Acme Corp"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Team Slug</label>
                                    <input
                                        type="text"
                                        required
                                        value={newTeamSlug}
                                        onChange={(e) => setNewTeamSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                        placeholder="acme-corp"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={creating || !newTeamName || !newTeamSlug}
                                        className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        {creating ? 'Creating...' : 'Create Team Workspace'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === 'members' && selectedTeam && (
                        <div>
                            {canManageMembers && <div className="mb-8">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Invite Members to {selectedTeam.name}</h3>
                                <form onSubmit={handleInviteUser} className="flex gap-3 items-end bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            placeholder="colleague@example.com"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                    </div>
                                    <div className="w-32">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                                        <select
                                            value={inviteRole}
                                            onChange={(e) => setInviteRole(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        >
                                            <option value="member">Member</option>
                                            <option value="admin">Admin</option>
                                            <option value="developer">Developer</option>
                                            <option value="viewer">Viewer</option>
                                        </select>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={inviting || !inviteEmail}
                                        className="py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
                                    >
                                        {inviting ? 'Sending...' : 'Send Invite'}
                                    </button>
                                </form>
                                {inviteSuccess && (
                                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 mt-0.5" />
                                        <span className="font-mono break-all">{inviteSuccess}</span>
                                    </div>
                                )}
                            </div>}

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members ({members.length})</h3>
                                {loadingMembers ? (
                                    <div className="text-center py-4 text-gray-500">Loading members...</div>
                                ) : (
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                                                    {canManageMembers && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {members.map(member => (
                                                    <tr key={member.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-700 font-bold">
                                                                    {member.email.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div className="ml-3">
                                                                    <div className="text-sm font-medium text-gray-900">{member.email}</div>
                                                                    {member.github_username && (
                                                                        <div className="text-xs text-gray-500">@{member.github_username}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {canManageMembers ? (
                                                                <select
                                                                    aria-label={`Role for ${member.email}`}
                                                                    value={member.role}
                                                                    disabled={busyMemberId === member.id || (member.role === 'owner' && selectedTeam.role !== 'owner' && user?.platform_role?.slug !== 'super_admin')}
                                                                    onChange={(event) => handleRoleChange(member, event.target.value)}
                                                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                                                >
                                                                    <option value="owner">Owner</option>
                                                                    <option value="admin">Admin</option>
                                                                    <option value="developer">Developer</option>
                                                                    <option value="member">Member</option>
                                                                    <option value="viewer">Viewer</option>
                                                                </select>
                                                            ) : (
                                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'owner' ? 'bg-purple-100 text-purple-800' : member.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                    {member.role}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {new Date(member.joined_at).toLocaleDateString()}
                                                        </td>
                                                        {canManageMembers && <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                            <button
                                                                type="button"
                                                                disabled={busyMemberId === member.id || member.role === 'owner'}
                                                                onClick={() => handleRemoveMember(member)}
                                                                className="text-red-600 hover:text-red-800 disabled:opacity-40"
                                                            >Remove</button>
                                                        </td>}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'members' && !selectedTeam && (
                        <div className="text-center py-8">
                            <p className="text-gray-500">Select a team from the header to manage its members.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamSettingsModal;
